import React, { useCallback, useEffect, useMemo, useState } from "react";
import oraclePhase27Api from "@/api/oraclePhase27Client";
import { logix2Dock } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Multi-Device Notification Fabric (Phase 27H + Phase 29D).
 *
 * Phase 27H: honest status board for each channel (configured vs awaiting-key).
 * Phase 29D: adds real client-side handlers per channel:
 *   • Laptop / Desktop  → Notification API local notification (research-only body)
 *   • Mobile Push       → Notification API + vibrate fallback (PWA-style)
 *   • Smartwatch        → Notification + `navigator.vibrate` pattern
 *   • TV / Large Display → fullscreen briefing preview window
 *   • Bluetooth Audio   → speak short briefing snippet via LOGIX-2 voice/speak
 *   • Email / SMS / Slack → call backend test-send endpoint (no-op if no key)
 *
 * Each handler still calls `/api/oracle/notifications/test-send` for backend
 * telemetry. Output remains research-only with v27 compliance lock.
 */

const COMPLIANCE_LINE = "Educational research only. Not financial / not betting advice. Human review required.";
const SAFE_BODY = (label) => `${label}: LOGIX-2 daily intelligence ping. ${COMPLIANCE_LINE}`;

function vibrate(pattern) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(pattern);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

async function ensureNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unavailable";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "denied";
  }
}

function fireLocalNotification({ title, body, tag, vibratePattern }) {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  if (Notification.permission !== "granted") return false;
  try {
    const n = new Notification(title, {
      body,
      tag: tag || "carelogix-oracle",
      requireInteraction: false,
      silent: false,
    });
    if (vibratePattern) vibrate(vibratePattern);
    setTimeout(() => { try { n.close(); } catch { /* ignore */ } }, 8000);
    return true;
  } catch {
    return false;
  }
}

function openFullscreenPreview() {
  if (typeof window === "undefined") return false;
  const w = window.open("", "_blank", "noopener,noreferrer,fullscreen=yes,width=1280,height=720");
  if (!w) return false;
  try {
    w.document.title = "CareLogix™ ORACLE — Briefing Cast";
    w.document.body.style.margin = "0";
    w.document.body.style.background = "linear-gradient(135deg,#040608,#0e3a2c)";
    w.document.body.style.color = "#fde68a";
    w.document.body.style.fontFamily = "system-ui, sans-serif";
    w.document.body.innerHTML = `
      <div style="display:flex;flex-direction:column;justify-content:center;align-items:center;height:100vh;text-align:center;padding:24px;">
        <div style="font-family:'Space Mono',monospace;letter-spacing:.2em;color:#c5a059;font-size:14px;">CareLogix™ ORACLE · Cast</div>
        <h1 style="margin:12px 0 4px;font-size:48px;color:#fde68a;">Daily CEO Briefing</h1>
        <p style="color:#cbd5e1;max-width:780px;font-size:18px;line-height:1.4;">
          Multi-asset intelligence brief. Stocks · Crypto · FX · Healthcare · Startups · Grants · Sports.
        </p>
        <p style="color:#94a3b8;max-width:760px;font-size:15px;font-style:italic;">
          ${COMPLIANCE_LINE}
        </p>
        <p style="margin-top:24px;color:#c5a059;font-family:'Space Mono',monospace;font-size:12px;">
          Tap or press F11 to enter fullscreen.
        </p>
      </div>`;
    return true;
  } catch {
    return false;
  }
}

async function speakAloud(text) {
  // Prefer server-side compliance-checked TTS; fall back to local speechSynthesis.
  try {
    const { data } = await logix2Dock.voiceSpeak({ text, provider: "browser", summary_only: true });
    const spoken = data?.spoken_text || data?.text || text;
    if (data?.provider === "elevenlabs" && data?.audio_b64) {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_b64}`);
      audio.play();
      return true;
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(spoken);
      u.rate = data?.rate || 1.0;
      u.pitch = data?.pitch || 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      return true;
    }
  } catch {
    // Try local browser TTS directly.
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance(text);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
        return true;
      }
    } catch { /* ignore */ }
  }
  return false;
}

export default function OracleNotificationFabric({
  testid = "oracle-notification-fabric",
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [testing, setTesting] = useState("");
  const [feedback, setFeedback] = useState("");
  const [permission, setPermission] = useState("default");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await oraclePhase27Api.notifChannels();
      setData(data);
    } catch (e) {
      setErr(e?.message || "channels_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setPermission(Notification.permission || "default");
    } else {
      setPermission("unavailable");
    }
  }, []);

  const channels = data?.channels || [];

  const runHandler = useCallback(async (channel) => {
    const id = channel.id;
    setTesting(id); setFeedback("");
    let local = ""; // result of the client-side action
    try {
      // Client-side action dispatch
      if (id === "laptop_browser_push" || id === "desktop_notification") {
        const perm = await ensureNotificationPermission();
        setPermission(perm);
        if (perm === "granted") {
          const ok = fireLocalNotification({
            title: "CareLogix ORACLE™",
            body: SAFE_BODY(channel.label),
            tag: id,
          });
          local = ok ? "Local notification fired." : "Local notification API unavailable.";
        } else {
          local = `Browser notification permission ${perm}.`;
        }
      } else if (id === "mobile_push") {
        const perm = await ensureNotificationPermission();
        setPermission(perm);
        if (perm === "granted") {
          fireLocalNotification({
            title: "CareLogix ORACLE™ — Mobile Push",
            body: SAFE_BODY(channel.label),
            tag: id,
            vibratePattern: [120, 60, 120],
          });
          local = "Local mobile-style push fired (browser notification + vibrate).";
        } else if (vibrate([200, 100, 200])) {
          local = `Notification permission ${perm}; fell back to vibrate.`;
        } else {
          local = `Notification permission ${perm}; vibrate not supported.`;
        }
      } else if (id === "smartwatch") {
        const perm = await ensureNotificationPermission();
        setPermission(perm);
        const vibrated = vibrate([80, 40, 80, 40, 200]);
        if (perm === "granted") {
          fireLocalNotification({
            title: "CareLogix ORACLE™ ⌚",
            body: SAFE_BODY("Smartwatch"),
            tag: id,
            vibratePattern: [80, 40, 80, 40, 200],
          });
          local = vibrated ? "Watch-style buzz + notification fired." : "Notification fired; vibrate not supported.";
        } else {
          local = vibrated ? "Vibrate buzz fired; notification permission denied." : "Watch fallback unavailable in this browser.";
        }
      } else if (id === "tv_screen") {
        const opened = openFullscreenPreview();
        local = opened
          ? "Cast preview window opened."
          : "Pop-up blocked — allow pop-ups for the cast preview.";
      } else if (id === "bluetooth_audio") {
        const ok = await speakAloud(`Bluetooth briefing. ${SAFE_BODY("Audio")}`);
        local = ok ? "Audio briefing dispatched to default audio output." : "Audio TTS unavailable.";
      } else if (id === "slack_webhook" || id === "sms_twilio" || id === "email") {
        local = `Backend send pipeline (key required server-side).`;
      } else {
        local = "Channel registered — no local handler defined.";
      }

      // Backend telemetry — always called (no-op when unconfigured).
      try {
        const { data: backendRes } = await oraclePhase27Api.notifTestSend(
          id,
          `[client-test] ${channel.label} — ${COMPLIANCE_LINE}`,
        );
        const tail = backendRes?.detail || (backendRes?.ok ? "Backend queued." : "Backend awaiting key.");
        setFeedback(`${channel.label}: ${local} ${tail}`);
      } catch (be) {
        setFeedback(`${channel.label}: ${local} (telemetry skipped — ${be?.message || "error"})`);
      }
    } catch (e) {
      setFeedback(`${channel.label}: ${e?.message || "handler_failed"}`);
    } finally { setTesting(""); }
  }, []);

  const requestPermissionBanner = useMemo(() => {
    if (permission === "granted" || permission === "unavailable") return null;
    return (
      <div style={S.permBanner} data-testid={`${testid}-perm-banner`}>
        <span>Notifications require permission to demo the laptop/desktop/mobile/watch handlers.</span>
        <button
          type="button"
          onClick={async () => setPermission(await ensureNotificationPermission())}
          style={S.permBtn}
          data-testid={`${testid}-perm-grant`}
        >
          Grant permission
        </button>
      </div>
    );
  }, [permission, testid]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Multi-Device Notification Fabric™ · Phase 29D handlers</span>
          <h3 style={S.title}>📡 Channels &amp; Status</h3>
        </div>
        <div style={S.headRight}>
          <span style={S.countChip} data-testid={`${testid}-count`}>
            {data ? `${data.configured_count}/${data.count} configured` : "—"}
          </span>
          <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>
            {busy ? "…" : "↻"}
          </button>
        </div>
      </header>

      {requestPermissionBanner}
      {err && <div style={S.err}>⚠ {err}</div>}

      <div style={S.grid}>
        {channels.map((c) => {
          const ok = c.configured;
          return (
            <div key={c.id} style={S.card} data-testid={`notif-channel-${c.id}`}>
              <div style={S.cardHead}>
                <span style={S.icon} aria-hidden="true">{c.icon}</span>
                <strong style={S.cardTitle}>{c.label}</strong>
              </div>
              {!compact && <p style={S.cardDesc}>{c.description}</p>}
              <div style={S.cardMeta}>
                <span
                  data-testid={`notif-status-${c.id}`}
                  style={{
                    ...S.statusPill,
                    color: ok ? "#10b981" : "#fcd34d",
                    borderColor: ok ? "#10b981" : "#fcd34d",
                    background: ok ? "rgba(16,185,129,0.10)" : "rgba(252,211,77,0.10)",
                  }}
                >
                  {c.status_label}
                </span>
                <button
                  type="button"
                  onClick={() => runHandler(c)}
                  disabled={testing === c.id}
                  style={S.tinyBtn}
                  data-testid={`notif-test-${c.id}`}
                >{testing === c.id ? "…" : "Send Test"}</button>
              </div>
            </div>
          );
        })}
      </div>
      {feedback && <div style={S.feedback} data-testid={`${testid}-feedback`}>{feedback}</div>}
      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.22)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  headRight: { display: "flex", alignItems: "center", gap: 6 },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 17 },
  countChip: { color: "#c5a059", border: "1px solid #c5a059", padding: "4px 9px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", fontWeight: 800 },
  refresh: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "4px 9px", borderRadius: 8, cursor: "pointer" },
  err: { padding: "6px 9px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, color: "#fecaca", fontSize: 12 },
  permBanner: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 10px", background: "rgba(252,211,77,0.08)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 8, color: "#fde68a", fontSize: 12, flexWrap: "wrap" },
  permBtn: { appearance: "none", background: "transparent", border: "1px solid #c5a059", color: "#c5a059", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontFamily: "'Space Mono', monospace", fontWeight: 800, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  cardHead: { display: "flex", alignItems: "center", gap: 8 },
  icon: { fontSize: 18 },
  cardTitle: { color: "#fde68a", fontSize: 13 },
  cardDesc: { color: "#94a3b8", fontSize: 11.5, lineHeight: 1.4, margin: 0 },
  cardMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, flexWrap: "wrap" },
  statusPill: { fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.06em", fontWeight: 800, padding: "3px 8px", border: "1px solid", borderRadius: 999 },
  tinyBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontSize: 10.5, fontFamily: "'Space Mono', monospace", fontWeight: 800 },
  feedback: { padding: "8px 11px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.30)", borderRadius: 8, color: "#d1fae5", fontSize: 12 },
};
